import React, { useState } from 'react';
import { Phone, MapPin, Clock, MessageCircle, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', subject: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message envoyé avec succès ! Maître Diamma vous contactera très bientôt.');
    setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const services = ['Retour Affectif', 'Voyance', 'Guérison', 'Protection', 'Chance', 'Réussite'];

  return (
    <section id="contact" className="py-14 bg-neutral-950 relative overflow-hidden">
      {/* Dot Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, rgb(212, 168, 67) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">Contact Marabout Genève</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            <span className="text-amber-400">Contacter Maître Diamma</span> - Marabout Africain Genève
          </h2>
          <p className="text-amber-100/60 max-w-2xl mx-auto text-lg">
            <strong>Consultation GRATUITE</strong> avec notre marabout sérieux à Genève. Spécialiste retour affectif rapide, voyant médium. Réponse immédiate au <strong className="text-amber-400">+41 76 783 61 86</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>Informations de Contact</h3>
            <div className="space-y-6">
              {/* Phone */}
              <div className="flex items-start space-x-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="contact-phone">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-amber-200/60 text-sm mb-1">Téléphone</div>
                  <a href="tel:+41767836186" className="hover:text-amber-400 transition-colors font-medium text-amber-400 text-lg">+41 76 783 61 86</a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4 p-4 rounded-xl" data-testid="contact-address">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-amber-200/60 text-sm mb-1">Adresse</div>
                  <div className="text-white font-medium">Rue du Mont-Blanc 14, 1201 Genève</div>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start space-x-4 p-4 rounded-xl" data-testid="contact-hours">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-amber-200/60 text-sm mb-1">Horaires</div>
                  <div className="text-white font-medium">Tous les jours de 8h à 23h30</div>
                </div>
              </div>
            </div>

            {/* Services List */}
            <div className="mt-8 p-6 bg-neutral-900/50 border border-amber-500/20 rounded-xl">
              <h4 className="text-white font-semibold mb-4">Services disponibles :</h4>
              <div className="grid grid-cols-2 gap-2">
                {services.map((s) => (
                  <a key={s} href="#services" className="text-sm text-amber-200/70 hover:text-amber-400 transition-colors flex items-center">
                    <ArrowRight className="w-3 h-3 mr-1" />{s}
                  </a>
                ))}
              </div>
            </div>

            {/* Urgent CTA */}
            <div className="mt-8 p-6 bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/30 rounded-2xl">
              <h4 className="text-xl font-semibold text-white mb-3">Consultation Urgente ?</h4>
              <p className="text-amber-200/70 mb-4">Appelez directement Maître Diamma pour une consultation immédiate. Retour affectif, voyance, désenvoûtement...</p>
              <a href="tel:+41767836186" className="inline-flex items-center justify-center w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold px-6 py-3 rounded-lg transition-all" data-testid="contact-urgent-btn">
                <Phone className="w-5 h-5 mr-2" />+41 76 783 61 86
              </a>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-gradient-to-br from-neutral-900 to-neutral-950 border border-amber-500/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>Envoyez un Message</h3>
            <p className="text-amber-200/60 text-sm mb-6">Contactez Maître Diamma, marabout à Genève, pour toute demande.</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-amber-200/80 mb-2 block text-sm font-medium">Nom complet *</label>
                  <input name="name" value={formData.name} onChange={handleChange} required placeholder="Votre nom"
                    className="w-full rounded-md border px-3 py-2 text-sm bg-black/50 border-amber-500/30 text-white placeholder:text-amber-200/40 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-amber-200/80 mb-2 block text-sm font-medium">Téléphone *</label>
                  <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="Votre téléphone"
                    className="w-full rounded-md border px-3 py-2 text-sm bg-black/50 border-amber-500/30 text-white placeholder:text-amber-200/40 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-amber-200/80 mb-2 block text-sm font-medium">Email</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Votre email (optionnel)"
                  className="w-full rounded-md border px-3 py-2 text-sm bg-black/50 border-amber-500/30 text-white placeholder:text-amber-200/40 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-amber-200/80 mb-2 block text-sm font-medium">Sujet de votre demande *</label>
                <input name="subject" value={formData.subject} onChange={handleChange} required placeholder="Ex: Retour affectif, Voyance, Désenvoûtement..."
                  className="w-full rounded-md border px-3 py-2 text-sm bg-black/50 border-amber-500/30 text-white placeholder:text-amber-200/40 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-amber-200/80 mb-2 block text-sm font-medium">Votre message *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} placeholder="Décrivez votre situation en quelques mots..."
                  className="w-full rounded-md border px-3 py-2 text-sm bg-black/50 border-amber-500/30 text-white placeholder:text-amber-200/40 focus:border-amber-500 focus:outline-none resize-none"
                />
              </div>

              <button type="submit" className="w-full inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold px-6 py-3 rounded-lg transition-all" data-testid="contact-submit-btn">
                <Send className="w-5 h-5 mr-2" />Envoyer le Message
              </button>

              <p className="text-amber-200/50 text-xs text-center">
                Discrétion absolue garantie - Vos informations restent confidentielles
              </p>
            </form>
          </div>
        </div>

        {/* Bottom SEO Links */}
        <div className="mt-16 p-6 bg-black/50 border border-amber-500/10 rounded-xl text-center">
          <p className="text-amber-200/40 text-sm">
            Maître Diamma - Marabout Genève - Voyant Médium Genève - Retour Affectif - Désenvoûtement - Guérisseur Spirituel - Marabout Africain - Marabout Sérieux
          </p>
        </div>
      </div>
    </section>
  );
};
